/**
 * Checklist helpers: ready-made task timelines counted back from the event date, and the sorting, status and
 * summary logic the checklist tool needs. Pure.
 */
import type { Task } from "./types";

export interface TemplateTask { title: string; weeksBefore: number; category: string }

export const TASK_TEMPLATES: Record<string, TemplateTask[]> = {
  Wedding: [
    { title: "Set budget and guest list", weeksBefore: 52, category: "Planning" },
    { title: "Book venue", weeksBefore: 44, category: "Venue" },
    { title: "Book photographer & caterer", weeksBefore: 36, category: "Vendors" },
    { title: "Send save-the-dates", weeksBefore: 26, category: "Guests" },
    { title: "Order invitations", weeksBefore: 16, category: "Guests" },
    { title: "Send invitations", weeksBefore: 10, category: "Guests" },
    { title: "Final headcount to caterer", weeksBefore: 2, category: "Catering" },
    { title: "Rehearsal", weeksBefore: 0.2, category: "Day of" },
  ],
  Birthday: [
    { title: "Pick theme and date", weeksBefore: 6, category: "Planning" },
    { title: "Book venue/reserve space", weeksBefore: 5, category: "Venue" },
    { title: "Send invitations", weeksBefore: 3, category: "Guests" },
    { title: "Order cake", weeksBefore: 2, category: "Catering" },
    { title: "Buy decorations", weeksBefore: 1, category: "Venue" },
    { title: "Confirm final headcount", weeksBefore: 0.5, category: "Guests" },
  ],
  "Corporate event": [
    { title: "Define goals and budget", weeksBefore: 12, category: "Planning" },
    { title: "Book venue", weeksBefore: 10, category: "Venue" },
    { title: "Open registration", weeksBefore: 8, category: "Guests" },
    { title: "Confirm speakers/agenda", weeksBefore: 6, category: "Programme" },
    { title: "Order catering & AV", weeksBefore: 3, category: "Vendors" },
    { title: "Send reminder emails", weeksBefore: 1, category: "Guests" },
  ],
  Conference: [
    { title: "Set goals, budget and date", weeksBefore: 36, category: "Planning" },
    { title: "Book venue and accommodation block", weeksBefore: 32, category: "Venue" },
    { title: "Open call for speakers", weeksBefore: 26, category: "Programme" },
    { title: "Open registration", weeksBefore: 20, category: "Guests" },
    { title: "Confirm sponsors and exhibitors", weeksBefore: 14, category: "Sponsors" },
    { title: "Publish the agenda", weeksBefore: 8, category: "Programme" },
    { title: "Order badges, signage and printed materials", weeksBefore: 4, category: "Production" },
    { title: "Brief speakers and volunteers", weeksBefore: 1, category: "Day of" },
    { title: "Final headcount and dietary list to caterer", weeksBefore: 1, category: "Catering" },
  ],
  "Exhibition or trade show": [
    { title: "Set goals, budget and floor plan", weeksBefore: 40, category: "Planning" },
    { title: "Book venue and contractors", weeksBefore: 34, category: "Venue" },
    { title: "Open exhibitor booking", weeksBefore: 28, category: "Exhibitors" },
    { title: "Launch visitor registration", weeksBefore: 14, category: "Visitors" },
    { title: "Send the exhibitor manual", weeksBefore: 8, category: "Exhibitors" },
    { title: "Confirm power, internet and rigging orders", weeksBefore: 5, category: "Production" },
    { title: "Print visitor badges and signage", weeksBefore: 2, category: "Production" },
    { title: "Build-up and final walk-through", weeksBefore: 0.3, category: "Day of" },
  ],
  Fundraiser: [
    { title: "Set the fundraising goal and budget", weeksBefore: 16, category: "Planning" },
    { title: "Book venue", weeksBefore: 14, category: "Venue" },
    { title: "Line up sponsors and auction items", weeksBefore: 10, category: "Sponsors" },
    { title: "Send invitations and ticket link", weeksBefore: 8, category: "Guests" },
    { title: "Arrange volunteers and donation handling", weeksBefore: 3, category: "Day of" },
    { title: "Confirm final headcount", weeksBefore: 1, category: "Guests" },
    { title: "Send thank-you messages", weeksBefore: -1, category: "After" },
  ],
};

export const TEMPLATE_NAMES = Object.keys(TASK_TEMPLATES);

const pad = (n: number) => String(n).padStart(2, "0");
export const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** "YYYY-MM-DD" as a local date at midnight, or null when it is not a real date. */
export function parseIso(s: string | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
}

/** Whole calendar days from a to b (DST-safe). */
export const daysBetween = (a: Date, b: Date) =>
  Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);

export const addDaysIso = (iso: string, n: number): string | null => {
  const d = parseIso(iso);
  return d ? isoOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)) : null;
};

/** The tasks of a template with due dates counted back from the event date. Empty if the date or name is not valid. */
export function tasksFromTemplate(name: string, eventDate: string, makeId: () => string): Task[] {
  const list = TASK_TEMPLATES[name];
  if (!list || !parseIso(eventDate)) return [];
  return list.map((t) => ({ id: makeId(), title: t.title, due: addDaysIso(eventDate, -Math.round(t.weeksBefore * 7))!, done: false, category: t.category }));
}

/** Not done first, by due date (undated last), then done tasks. */
export function sortTasks(list: readonly Task[]): Task[] {
  return [...list].sort((a, b) =>
    Number(a.done) - Number(b.done) || (a.due ?? "9999-99-99").localeCompare(b.due ?? "9999-99-99") || a.title.localeCompare(b.title));
}

export type DueState = "done" | "overdue" | "today" | "soon" | "later" | "undated";

/** `soon` means within the next seven days. */
export function dueState(t: Task, today: string): DueState {
  if (t.done) return "done";
  if (!t.due) return "undated";
  if (t.due < today) return "overdue";
  if (t.due === today) return "today";
  const diff = daysBetween(parseIso(today)!, parseIso(t.due)!);
  return diff <= 7 ? "soon" : "later";
}

export interface TaskSummary { total: number; done: number; open: number; overdue: number; dueSoon: number; percent: number }

export function summarizeTasks(list: readonly Task[], today: string): TaskSummary {
  const states = list.map((t) => dueState(t, today));
  const done = states.filter((s) => s === "done").length;
  return {
    total: list.length, done, open: list.length - done,
    overdue: states.filter((s) => s === "overdue").length,
    dueSoon: states.filter((s) => s === "today" || s === "soon").length,
    percent: list.length ? Math.round((done / list.length) * 100) : 0,
  };
}

/** Moves every task that has a due date by a number of days (for when the event date changes). */
export function shiftDueDates(list: readonly Task[], days: number): Task[] {
  return list.map((t) => (t.due ? { ...t, due: addDaysIso(t.due, days) ?? t.due } : t));
}

/** Brings over ticks saved by the earlier checklist tool (`Type:task` keys) for tasks that match by title. */
export function migrateLegacyDone(doneKeys: unknown, tasks: readonly Task[], type: string): Task[] {
  if (!Array.isArray(doneKeys)) return [...tasks];
  const done = new Set(doneKeys.filter((k): k is string => typeof k === "string"));
  return tasks.map((t) => (done.has(`${type}:${t.title}`) ? { ...t, done: true } : t));
}
